class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this._checkoutAllowed = false;
    this.addEventListener('click', this.onCheckoutClick.bind(this), { capture: true });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  async onCheckoutClick(evt) {
    const btn = evt.target.closest('#CartDrawer-Checkout');
    if (!btn) return;
    if (this._checkoutAllowed) return;
    if (!this.validateMinimumAmount(evt)) return;
    if (!window.keysouqVerifyStock) return;

    evt.preventDefault();
    evt.stopImmediatePropagation();

    btn.setAttribute('disabled', true);
    btn.classList.add('loading');
    btn.querySelector('.loading__spinner').classList.remove('hidden');
    this.showCheckoutError('');

    try {
      const cart = await fetch('/cart.js').then((r) => r.json());
      const productIds = [...new Set(cart.items.map((item) => String(item.product_id)))];

      const response = await fetch('/apps/service', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action_type: 'verify_stock', gids: productIds }),
      });
      const stockData = await response.json();

      const outOfStock = Array.isArray(stockData.data)
        ? stockData.data.filter((item) => !item.is_in_stock)
        : [];

      btn.removeAttribute('disabled');
      btn.classList.remove('loading');
      btn.querySelector('.loading__spinner').classList.add('hidden');

      if (outOfStock.length === 0) {
        this._checkoutAllowed = true;
        btn.click();
        this._checkoutAllowed = false;
      } else {
        const outOfStockIds = new Set(outOfStock.map((item) => String(item.guid)));
        this.querySelectorAll('.cart-item-stock-error').forEach((errorEl) => {
          errorEl.hidden = true;
        });
        this.querySelectorAll('[data-product-id]').forEach((itemEl) => {
          if (outOfStockIds.has(String(itemEl.dataset.productId))) {
            const errorEl = itemEl.querySelector('.cart-item-stock-error');
            if (errorEl) errorEl.hidden = false;
          }
        });
        this.showCheckoutError('Some items in your cart are currently out of stock. Please remove them before checking out.');
      }
    } catch {
      btn.removeAttribute('disabled');
      btn.classList.remove('loading');
      btn.querySelector('.loading__spinner').classList.add('hidden');
      this._checkoutAllowed = true;
      btn.click();
      this._checkoutAllowed = false;
    }
  }

  validateMinimumAmount(evt) {
    const minCart = window.keysouqMinCart;
    if (!minCart || typeof minCart.validate !== 'function') return true;

    return minCart.validate(evt, this, this.querySelector('#CartDrawer-MinCartError'));
  }

  showCheckoutError(message) {
    let errorEl = this.querySelector('#CartDrawer-StockError');
    if (!errorEl) {
      errorEl = document.createElement('p');
      errorEl.id = 'CartDrawer-StockError';
      errorEl.style.cssText = 'color:red;font-size:13px;margin:8px 0 0;text-align:center;';
      const minCartError = this.querySelector('#CartDrawer-MinCartError');
     if (minCartError) minCartError.insertAdjacentElement('afterend', errorEl);
    }
    errorEl.textContent = message;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
